import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './SigningPage.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5134';

// Fallback if the font-options endpoint can't be reached. Keys match the API's SignatureFonts.
const FALLBACK_FONTS = [
  { key: 'great-vibes', display: 'Great Vibes', webFamily: 'Great Vibes' },
  { key: 'allura',      display: 'Allura',      webFamily: 'Allura' },
  { key: 'sacramento',  display: 'Sacramento',  webFamily: 'Sacramento' },
];

export default function SigningPage() {
  const { token }      = useParams();
  const [searchParams] = useSearchParams();
  const returnUrl      = searchParams.get('returnUrl');
  const [contract, setContract] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [signing, setSigning]   = useState(false);
  const [signed, setSigned]     = useState(false);
  const [agreed, setAgreed]     = useState(false);

  // Signature method: typed cursive (default) or drawn.
  const [mode, setMode]     = useState('typed');
  const [sigName, setSigName] = useState('');
  const [sigFont, setSigFont] = useState('allura');
  const [fonts, setFonts]     = useState(FALLBACK_FONTS);

  const [hasSigned, setHasSigned] = useState(false);
  const canvasRef = useRef(null);
  const drawing   = useRef(false);

  useEffect(() => {
    fetch(`${BASE}/contracts/sign/${token}`)
      .then(r => { if (!r.ok) throw new Error('Contract not found or already signed.'); return r.json(); })
      .then(c => { setContract(c); setSigName(c.clientName || ''); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  useEffect(() => {
    fetch(`${BASE}/contracts/signature-fonts`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.length) setFonts(data); })
      .catch(() => { /* keep fallback */ });
  }, []);

  // --- draw-mode canvas ---
  function getPos(e) {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const src    = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  }
  function startDraw(e) {
    drawing.current = true;
    setHasSigned(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function draw(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#1a2433';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function stopDraw() { drawing.current = false; }
  function clearSig() {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  }

  const fontFamily = (key) => {
    const f = fonts.find(x => x.key === key) || fonts[0];
    return `'${f?.webFamily || 'Allura'}', cursive`;
  };

  async function handleSign() {
    if (!agreed) { setError('Please check the agreement box to continue.'); return; }

    let payload;
    if (mode === 'typed') {
      if (!sigName.trim()) { setError('Please type your name to adopt a signature.'); return; }
      payload = { type: 'typed', name: sigName.trim(), font: sigFont };
    } else {
      if (!hasSigned) { setError('Please draw your signature.'); return; }
      payload = { type: 'drawn', name: sigName.trim() || null, signatureData: canvasRef.current.toDataURL('image/png') };
    }

    setSigning(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/contracts/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed to submit signature.'); }
      setSigned(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSigning(false);
    }
  }

  if (loading) return <div className="signing-page"><div className="sign-loading">Loading contract…</div></div>;
  if (error && !contract) return <div className="signing-page"><div className="sign-error">{error}</div></div>;
  if (signed) return (
    <div className="signing-page">
      <div className="sign-success">
        <div className="success-icon">✓</div>
        <h2>Contract Signed</h2>
        <p>Thank you, {contract?.clientName}. A fully signed copy has been emailed to you for your records.</p>
        {returnUrl && (
          <a href={returnUrl} className="sign-return-link">← Return to your portal</a>
        )}
      </div>
    </div>
  );
  if (contract?.status === 'Signed') return (
    <div className="signing-page">
      <div className="sign-success">
        <div className="success-icon">✓</div>
        <h2>Already Signed</h2>
        <p>This contract was already signed on {new Date(contract.signedAt).toLocaleDateString()}.</p>
      </div>
    </div>
  );

  return (
    <div className="signing-page">
      <div className="signing-container">
        <div className="signing-header">
          <div className="company-name">Semper Gratus Inc.</div>
          <h1>{contract.title}</h1>
          <p className="signing-subtitle">Prepared for {contract.clientName}{contract.company ? ` · ${contract.company}` : ''}</p>
        </div>

        <div className="contract-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.body) }} />

        {(contract.setupFee > 0 || contract.monthlyFee > 0) && (
          <div className="fee-summary">
            <div className="fee-title">Fee Summary</div>
            {contract.setupFee > 0 && <div className="fee-row"><span>Setup Fee</span><span>${contract.setupFee.toFixed(2)}</span></div>}
            {contract.monthlyFee > 0 && <div className="fee-row"><span>Monthly Fee</span><span>${contract.monthlyFee.toFixed(2)}/mo</span></div>}
          </div>
        )}

        <div className="sig-section">
          <div className="sig-heading">Adopt your signature</div>

          <div className="sig-tabs" role="tablist">
            <button
              className={`sig-tab ${mode === 'typed' ? 'active' : ''}`}
              onClick={() => { setMode('typed'); setError(null); }}
            >Type</button>
            <button
              className={`sig-tab ${mode === 'drawn' ? 'active' : ''}`}
              onClick={() => { setMode('drawn'); setError(null); setHasSigned(false); }}
            >Draw</button>
          </div>

          {mode === 'typed' ? (
            <div className="sig-typed">
              <label className="sig-field">
                <span>Full legal name</span>
                <input type="text" value={sigName} onChange={e => setSigName(e.target.value)} placeholder="Your name" />
              </label>

              <div className="sig-preview" style={{ fontFamily: fontFamily(sigFont) }}>
                {sigName.trim() || 'Your signature'}
              </div>

              <div className="sig-font-grid">
                {fonts.map(f => (
                  <button
                    key={f.key}
                    className={`sig-font-card ${sigFont === f.key ? 'selected' : ''}`}
                    onClick={() => setSigFont(f.key)}
                    type="button"
                  >
                    <span className="sig-font-sample" style={{ fontFamily: `'${f.webFamily}', cursive` }}>
                      {sigName.trim() || 'Signature'}
                    </span>
                    <span className="sig-font-name">{f.display}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="sig-draw">
              <div className="canvas-wrap">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={stopDraw}
                />
                {!hasSigned && (
                  <div className="sig-guide" aria-hidden="true">
                    <svg className="sig-pen-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                    </svg>
                    <span className="sig-guide-text">Sign here</span>
                    <div className="sig-baseline" />
                  </div>
                )}
              </div>
              <div className="sig-actions">
                <button className="btn-clear" onClick={clearSig}>Clear signature</button>
              </div>
            </div>
          )}

          <label className="agree-row">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
            <span>I agree to the terms above and understand that adopting this signature constitutes a legal electronic signature.</span>
          </label>

          {error && <div className="sign-error-inline">{error}</div>}

          <button className="btn-sign" onClick={handleSign} disabled={signing || !agreed}>
            {signing ? 'Submitting…' : 'Adopt & Sign'}
          </button>
        </div>
      </div>
    </div>
  );
}
